import React, { useCallback, useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectAccountOnboarding, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { stripeConnectApi } from "../api/stripeConnectApi";
import { Alert, Badge, Button, Card, CardBody, CardHeader, cn } from "./ui";

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLIC_KEY;

const STATUS_COPY = {
  not_started: { title: "Set up payouts", description: "Complete Stripe's secure setup so Giggs can transfer your earnings to your bank account." },
  onboarding_incomplete: { title: "Payout setup incomplete", description: "Continue the secure Stripe setup and provide any remaining details." },
  verification_pending: { title: "Verification in progress", description: "Stripe is reviewing your details. We will update this status automatically." },
  action_required: { title: "Payout details required", description: "Stripe needs more information. Open the secure form to complete the outstanding requirements." },
  restricted: { title: "Payout account restricted", description: "Transfers or payouts are paused until the requested Stripe information is provided." },
  rejected: { title: "Payout account unavailable", description: "Stripe could not enable this payout account. Open the secure form for the available resolution or contact support." },
  payment_ready: { title: "Payout account ready", description: "Your Stripe payout account is ready to receive earnings from completed jobs." },
};

const PAYOUT_STATUS = {
  paid: { label: "Paid", variant: "success", arrived: true },
  in_transit: { label: "On the way", variant: "warning", arrived: false },
  pending: { label: "Scheduled", variant: "warning", arrived: false },
  canceled: { label: "Canceled", variant: "danger", arrived: false },
  failed: { label: "Failed", variant: "danger", arrived: false },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const StripeConnectOnboarding = ({ verificationStatus = "pending", verificationLoading = false }) => {
  const [accountStatus, setAccountStatus] = useState(null);
  const [connectInstance, setConnectInstance] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [payoutMsg, setPayoutMsg] = useState("");
  const [payoutErr, setPayoutErr] = useState("");
  const [payouts, setPayouts] = useState(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsError, setPayoutsError] = useState("");

  const isPlatformApproved = verificationStatus === "approved";
  const platformApprovalCopy = verificationStatus === "rejected"
    ? { title: "Profile changes required", description: "Resolve the requested profile or document changes and wait for Giggs approval before setting up payouts." }
    : { title: "Profile approval required", description: "Your public Gigger profile must be approved before Stripe payout setup becomes available." };

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const response = await stripeConnectApi.getAccountStatus();
      setAccountStatus(response?.data || null);
      setError("");
      return response?.data || null;
    } catch (statusError) {
      setError(statusError.message || "Unable to load your payout status.");
      return null;
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const statusKey = accountStatus?.status || "not_started";
  const isReady = statusKey === "payment_ready";
  const needsAction = ["action_required", "restricted", "rejected"].includes(statusKey);
  const isUnderReview = statusKey === "verification_pending";

  useEffect(() => {
    if (!isPlatformApproved) {
      setAccountStatus(null);
      setConnectInstance(null);
      setLoadingStatus(false);
      setError("");
      return;
    }
    refreshStatus();
  }, [isPlatformApproved, refreshStatus]);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await stripeConnectApi.balance();
      setBalance(res?.data || null);
    } catch {
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const loadPayouts = useCallback(async () => {
    setPayoutsLoading(true);
    setPayoutsError("");
    try {
      const res = await stripeConnectApi.bankPayouts();
      setPayouts(res?.data?.payouts || []);
    } catch (err) {
      setPayoutsError(err.response?.data?.message || err.message || "Couldn't load your payouts.");
      setPayouts([]);
    } finally {
      setPayoutsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isReady) {
      loadBalance();
      loadPayouts();
    }
  }, [isReady, loadBalance, loadPayouts]);

  const requestPayout = useCallback(async () => {
    setPayoutErr("");
    setPayoutMsg("");
    setPayoutBusy(true);
    try {
      const res = await stripeConnectApi.requestPayout();
      setPayoutMsg(res?.message || "Payout requested. It's on its way to your bank.");
      await loadBalance();
      await loadPayouts();
    } catch (err) {
      setPayoutErr(err.response?.data?.message || "Couldn't request a payout.");
    } finally {
      setPayoutBusy(false);
    }
  }, [loadBalance, loadPayouts]);

  const startOnboarding = useCallback(async () => {
    if (!isPlatformApproved) {
      setError("Your profile must be approved by Giggs before setting up payouts.");
      return;
    }
    if (!publishableKey) {
      setError("Stripe publishable key is missing. Add REACT_APP_STRIPE_PUBLISHABLE_KEY to the frontend environment.");
      return;
    }

    setStarting(true);
    setError("");
    try {
      await stripeConnectApi.createAccount();
      const fetchClientSecret = async () => {
        const response = await stripeConnectApi.createAccountSession();
        const clientSecret = response?.data?.clientSecret;
        if (!clientSecret) throw new Error("Stripe did not return an onboarding client secret.");
        return clientSecret;
      };
      const instance = loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret,
        appearance: {
          overlays: "dialog",
          variables: {
            colorPrimary: "#209668",
            colorText: "#1F2D26",
            colorBackground: "#FFFFFF",
            borderRadius: "10px",
            fontFamily: "inherit",
          },
        },
        locale: "en-AU",
      });
      setConnectInstance(instance);
      await refreshStatus();
    } catch (startError) {
      setError(startError.message || "Unable to start Stripe payout setup.");
    } finally {
      setStarting(false);
    }
  }, [isPlatformApproved, refreshStatus]);

  const handleExit = useCallback(async () => {
    setConnectInstance(null);
    const latest = await refreshStatus();
    if (latest?.status !== "payment_ready" && latest?.status !== "verification_pending") {
      setError("Your progress was saved, but payout setup still needs attention. You can continue when ready.");
    } else {
      setError("");
    }
  }, [refreshStatus]);

  const handleLoadError = useCallback((loadError) => {
    setError(loadError?.error?.message || loadError?.message || "Stripe onboarding could not load. Please try again.");
  }, []);

  const statusCopy = isPlatformApproved ? STATUS_COPY[statusKey] || STATUS_COPY.onboarding_incomplete : platformApprovalCopy;
  const statusLabel = !isPlatformApproved
    ? verificationStatus === "rejected" ? "Profile action required" : "Awaiting profile approval"
    : isReady ? "Payment ready" : needsAction ? "Action required" : isUnderReview ? "Under review" : "Setup required";
  const statusVariant = !isPlatformApproved || needsAction ? "warning" : isReady ? "success" : isUnderReview ? "info" : "neutral";
  const outstandingCount = (accountStatus?.currentlyDue?.length || 0) + (accountStatus?.pastDue?.length || 0);
  const availableAmount = balance?.available ?? 0;
  const pendingAmount = balance?.pending ?? 0;

  const guideSteps = [
    { title: "Get your Giggs profile approved", description: "Complete your profile and upload the required Giggs verification documents. Payout setup unlocks after admin approval.", complete: isPlatformApproved },
    { title: "Prepare your personal and business details", description: "Use your real legal name, date of birth, residential address, phone number and service details. Add an ABN only if you have one—never enter a false number." },
    { title: "Complete the secure Stripe form", description: "Select Set up payouts and answer Stripe's questions. Your approved public Gigger profile is supplied as your business website, so you do not need your own website." },
    { title: "Add your bank account", description: "Enter the correct account holder name, BSB and account number for the Australian bank account where you want to receive earnings." },
    { title: "Submit and monitor your status", description: "Stripe may verify your details immediately or request more information. Return to this Account tab if the status shows Action required.", complete: isUnderReview || isReady },
    { title: "Payout account ready", description: "When the status changes to Payment ready, Giggs can transfer eligible earnings to your connected bank account under the platform's payout terms.", complete: isReady },
  ];

  return (
    <div className="flex flex-col gap-5 font-dm">
      <Card className="shadow-none">
        <CardHeader
          title="Payout setup guide"
          description="Follow these steps carefully. Stripe uses this information to verify your identity and send your Giggs earnings to the correct bank account."
          action={<Badge variant="neutral">About 5–10 minutes</Badge>}
        />
        <CardBody>
          <Alert variant="warning" title="Use accurate information.">
            Your name, date of birth, address and bank details must be real and should match your official records. Incorrect details can delay or restrict payouts.
          </Alert>
          <ol className="mt-5 flex list-none flex-col gap-4 p-0">
            {guideSteps.map((step, index) => (
              <li key={step.title} className="flex gap-3">
                <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-dm text-xs font-bold', step.complete ? 'border-giggs-green bg-giggs-green text-white' : 'border-slate-200 bg-slate-50 text-giggs-text')}>
                  {step.complete ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg> : index + 1}
                </span>
                <div className="min-w-0">
                  <h4 className="m-0 font-nunito text-sm font-extrabold text-giggs-ink">{step.title}</h4>
                  <p className="mb-0 mt-1 text-xs leading-5 text-giggs-muted sm:text-[13px]">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-5 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-5 text-giggs-text">
            <strong>What to have ready:</strong> a valid ID, your current address, your Australian bank account details and your ABN only if you have one. Stripe may request additional documents depending on its verification checks.
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-none">
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant}>{statusLabel}</Badge>
            {!loadingStatus && !isReady && outstandingCount > 0 && <Badge variant="warning">{outstandingCount} requirement{outstandingCount === 1 ? "" : "s"} remaining</Badge>}
          </div>
          <h3 className="mb-1 mt-4 font-nunito text-xl font-extrabold text-giggs-ink">{statusCopy.title}</h3>
          <p className="mt-0 text-sm leading-6 text-giggs-muted">{statusCopy.description}</p>
          {error && <Alert variant="danger" className="mt-4">{error}</Alert>}

          {!connectInstance && (
            <Button className="mt-4 w-full sm:w-auto" onClick={startOnboarding} disabled={starting || loadingStatus || verificationLoading || !isPlatformApproved}>
              {verificationLoading ? "Checking profile approval…" : !isPlatformApproved ? "Available after profile approval" : starting ? "Opening secure setup…" : isReady ? "Review payout details" : needsAction ? "Fix payout account" : accountStatus?.accountId ? "Continue payout setup" : "Set up payouts"}
            </Button>
          )}

          {isReady && !connectInstance && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold text-giggs-muted">Available now</div>
                  <div className="mt-1 font-nunito text-2xl font-extrabold text-giggs-ink">{balanceLoading && !balance ? "…" : `${money(availableAmount)} AUD`}</div>
                </div>
                <Badge variant="warning">{money(pendingAmount)} pending</Badge>
              </div>
              <Button className="mt-4 w-full" onClick={requestPayout} disabled={payoutBusy || balanceLoading || !(availableAmount > 0)}>
                {payoutBusy ? "Requesting…" : availableAmount > 0 ? `Request payout of ${money(availableAmount)}` : "No funds available yet"}
              </Button>
              {payoutMsg && <Alert variant="success" className="mt-3">{payoutMsg}</Alert>}
              {payoutErr && <Alert variant="danger" className="mt-3">{payoutErr}</Alert>}
              <p className="mb-0 mt-3 text-xs leading-5 text-giggs-muted">Released earnings appear as <strong>pending</strong> first, then become <strong>available</strong> after a short settling period. Request a payout of your available balance and Stripe sends it to your bank.</p>
            </div>
          )}

          {isReady && !connectInstance && (
            <div className="mt-5 border-t border-slate-100 pt-5">
              <div className="flex items-center justify-between gap-3">
                <h4 className="m-0 font-nunito text-base font-extrabold text-giggs-ink">Bank payouts</h4>
                <Button size="sm" variant="outline" onClick={loadPayouts} disabled={payoutsLoading}>{payoutsLoading ? "Refreshing…" : "Refresh"}</Button>
              </div>
              {payoutsError && <Alert variant="danger" className="mt-3">{payoutsError}</Alert>}
              {payoutsLoading && !payouts ? (
                <p className="py-5 text-sm text-giggs-muted">Loading…</p>
              ) : !payouts || payouts.length === 0 ? (
                <p className="py-5 text-sm leading-6 text-giggs-muted">No bank payouts yet. Request a payout above and it will appear here with its expected arrival date.</p>
              ) : (
                <ul className="mt-4 list-none divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white p-0">
                  {payouts.map((payout) => {
                    const meta = PAYOUT_STATUS[payout.status] || { label: payout.status, variant: "neutral", arrived: false };
                    return (
                      <li key={payout.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <div className="font-nunito text-sm font-extrabold text-giggs-ink">{money(payout.amount)} {String(payout.currency).toUpperCase()}</div>
                          <div className="mt-0.5 text-xs text-giggs-muted">{meta.arrived ? "Arrived" : "Expected"} {fmtDate(payout.arrivalDate)}</div>
                        </div>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="mb-0 mt-3 text-xs leading-5 text-giggs-muted">Arrival dates account for weekends and bank holidays. Your identity and bank details are collected securely by Stripe.</p>
            </div>
          )}

          <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-giggs-muted">
            Your identity and bank details are collected securely by Stripe and are not stored by Giggs.
          </div>
        </CardBody>
      </Card>

      {isPlatformApproved && connectInstance && (
        <Card className="overflow-hidden p-3 shadow-none sm:p-5">
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectAccountOnboarding onExit={handleExit} onLoadError={handleLoadError} />
          </ConnectComponentsProvider>
        </Card>
      )}
    </div>
  );
};

export default StripeConnectOnboarding;
